import React, { useState, useMemo } from 'react';
import { formatDublinDate } from '../../utils/date';
import { ScopedLink } from '../../components/ScopedLink';
import ConfirmDialog from '../../components/ConfirmDialog';
import {
  useDeleteFormMutation,
  useGetFormDistributionStatsQuery,
  useCloseDistributionMutation,
} from '../../store/api/localApi';
import {
  FormType,
  FormStatus,
  FormDefinition,
  FormDistribution,
  DistributionStatus,
  Location,
} from '../../types';
import {
  PlusIcon,
  DocumentTextIcon,
  PencilIcon,
  DocumentDuplicateIcon,
  MapPinIcon,
  ClockIcon,
  CheckIcon,
  GlobeAltIcon,
  ArchiveBoxIcon,
} from '@heroicons/react/24/outline';
import PublishToLocationsModal from './PublishToLocationsModal';

// ── Constants ──────────────────────────────────────────────────────────────────

// The org-managed template types.
//
// STUDENT_INTAKE belongs here even though per-child intake RESPONSES are
// location-level: this view lists TEMPLATES, and the org owns the intake
// template — the ECCE pre-registration form is seeded org-owned precisely so an
// org can edit it once and publish it per location (see provisionEcceIntakeForm).
// Excluding it would make the seeded form invisible everywhere: this page would
// filter it out, and a location only shows org forms that have a
// locationStatuses entry, which a freshly seeded template does not yet have.
type OrgManagedFormType = FormType.REGISTRATION | FormType.CONSENT | FormType.STUDENT_INTAKE;

const formTypeLabels: Record<OrgManagedFormType, string> = {
  [FormType.REGISTRATION]: 'Registration Form',
  [FormType.CONSENT]: 'Consent Forms',
  [FormType.STUDENT_INTAKE]: 'Intake Forms',
};

const distributionStatusColors: Record<DistributionStatus, string> = {
  [DistributionStatus.ACTIVE]: 'bg-green-100 text-green-800',
  [DistributionStatus.CLOSED]: 'bg-gray-100 text-gray-800',
  [DistributionStatus.EXPIRED]: 'bg-red-100 text-red-800',
};

/** Types that only allow one active form (singletons) */
const SINGLETON_TYPES = [FormType.REGISTRATION];

/** Ordered list of form type sections (org-managed types only) */
const FORM_TYPE_ORDER: OrgManagedFormType[] = [
  FormType.REGISTRATION,
  FormType.CONSENT,
  FormType.STUDENT_INTAKE,
];

/** Exported for the regression test that pins STUDENT_INTAKE's presence. */
export const ORG_MANAGED_FORM_TYPES = FORM_TYPE_ORDER;

const getFormIcon = (formType: FormType) => {
  switch (formType) {
    case FormType.REGISTRATION:
      return DocumentDuplicateIcon;
    default:
      return DocumentTextIcon;
  }
};

// ── Shared sub-components ──────────────────────────────────────────────────────

/** Expandable distribution list for a single form */
const FormDistributions: React.FC<{ formId: string; currentFormVersion?: number }> = ({
  formId,
  currentFormVersion,
}) => {
  const { data: statsResponse, isLoading } = useGetFormDistributionStatsQuery(formId);
  const distributions = statsResponse?.distributions || [];
  const stats = statsResponse?.stats;
  const [closeDistribution, { isLoading: isClosing }] = useCloseDistributionMutation();
  const [pendingCloseId, setPendingCloseId] = useState<string | null>(null);

  const handleClose = async (distributionId: string) => {
    try {
      await closeDistribution({ distributionId, formId }).unwrap();
    } catch (err: any) {
      window.alert(err?.data?.message || err?.message || 'Failed to close distribution');
    } finally {
      setPendingCloseId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="px-6 py-3 bg-gray-50 text-sm text-gray-500">Loading distributions...</div>
    );
  }

  if (!distributions || distributions.length === 0) {
    return (
      <div className="px-6 py-3 bg-gray-50 text-sm text-gray-500">
        No distributions yet. Use &ldquo;Send&rdquo; to distribute this form.
      </div>
    );
  }

  return (
    <div className="bg-gray-50 border-t border-gray-200">
      {stats && stats.totalSent > 0 && (
        <div className="px-6 py-3 border-b border-gray-200">
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
            Overall response rate
          </h4>
          <div className="flex items-center space-x-6 text-sm">
            <span className="text-gray-700">
              <span className="font-medium text-gray-900">{stats.totalCompleted}</span>
              <span className="text-gray-500"> / {stats.totalSent} completed</span>
            </span>
            <span className="text-gray-500">{stats.totalPending} pending</span>
          </div>
        </div>
      )}
      <div className="px-6 py-2">
        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
          Distributions
        </h4>
      </div>
      <ul className="divide-y divide-gray-200">
        {distributions.map((dist: FormDistribution) => {
          const label =
            dist.eventName ||
            `Sent to ${
              dist.targetType === 'LOCATION'
                ? 'entire location'
                : dist.targetType === 'CLASS'
                ? 'class'
                : 'selected students'
            }`;

          const isOlderVersion =
            currentFormVersion !== undefined && dist.formVersion !== currentFormVersion;

          return (
            <li key={dist.id} className="px-6 py-3">
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900 truncate">{label}</p>
                  <div className="flex items-center space-x-3 mt-1 text-xs text-gray-500">
                    <span>{formatDublinDate(dist.sentAt)}</span>
                    <span>
                      {dist.completedCount}/{dist.totalInvitations} responses
                    </span>
                    {dist.deadline && (
                      <span className="flex items-center">
                        <ClockIcon className="h-3 w-3 mr-1" />
                        Due {formatDublinDate(dist.deadline)}
                      </span>
                    )}
                  </div>
                  {isOlderVersion && (
                    <p className="mt-1 text-xs text-amber-600">
                      Filled on version {dist.formVersion} (current: version {currentFormVersion})
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      distributionStatusColors[dist.status]
                    }`}
                  >
                    {dist.status}
                  </span>
                  {dist.status === DistributionStatus.ACTIVE && (
                    <button
                      type="button"
                      data-testid={`distribution-close-btn-${dist.id}`}
                      disabled={isClosing}
                      onClick={() => setPendingCloseId(dist.id)}
                      className="inline-flex items-center px-2 py-0.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      Close
                    </button>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
      <ConfirmDialog
        isOpen={!!pendingCloseId}
        title="Close distribution?"
        message="Pending invitations will be cancelled — parents won't be able to fill this any more. Already-submitted responses are unaffected."
        confirmLabel="Close distribution"
        variant="danger"
        onConfirm={() => pendingCloseId && handleClose(pendingCloseId)}
        onCancel={() => setPendingCloseId(null)}
      />
    </div>
  );
};

// ── OrgFormCard ────────────────────────────────────────────────────────────────

const OrgFormCard: React.FC<{
  form: FormDefinition;
  locations: Location[];
  onPublish: (form: FormDefinition) => void;
  onDelete: (formId: string) => void;
}> = ({ form, locations, onPublish, onDelete }) => {
  const Icon = getFormIcon(form.formType);
  const publishedCount = form.locationIds?.length || 0;
  const totalLocations = locations.length;
  const publishedLabel =
    publishedCount === 0
      ? 'Not published'
      : publishedCount === totalLocations
      ? 'All locations'
      : `${publishedCount} location${publishedCount !== 1 ? 's' : ''}`;

  // Per-location active status count
  const activeLocationCount = form.locationStatuses
    ? Object.values(form.locationStatuses).filter((s) => s === FormStatus.ACTIVE).length
    : 0;
  const publishedLocationCount = form.locationIds?.length || 0;
  const locationStatusLabel =
    publishedLocationCount === 0
      ? 'Not published'
      : activeLocationCount === publishedLocationCount
      ? `Active at all ${publishedLocationCount} locations`
      : `Active at ${activeLocationCount}/${publishedLocationCount} locations`;

  return (
    <div
      data-testid={`form-card-${form.id}`}
      className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0 h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
            <Icon className="h-5 w-5 text-primary-600" />
          </div>
          <div>
            <div className="flex items-center flex-wrap gap-2">
              <h4 className="text-sm font-medium text-gray-900">{form.title}</h4>
              {form.title === 'Default Registration Form' && (
                <span
                  data-testid={`form-default-badge-${form.id}`}
                  className="inline-flex items-center rounded-full bg-primary-50 px-2 py-0.5 text-xs font-medium text-primary-700"
                >
                  Default
                </span>
              )}
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                  activeLocationCount > 0
                    ? 'bg-green-100 text-green-800'
                    : publishedLocationCount > 0
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-gray-100 text-gray-800'
                }`}
              >
                {activeLocationCount > 0 && <CheckIcon className="h-3 w-3 mr-0.5" />}
                {locationStatusLabel}
              </span>
            </div>
            <div className="mt-1 flex items-center text-xs text-gray-500 space-x-2">
              <span className="flex items-center">
                <GlobeAltIcon className="h-3 w-3 mr-1" />
                Published to: {publishedLabel}
              </span>
              <span>&middot;</span>
              <span>v{form.version}</span>
              <span>&middot;</span>
              <span>{form.sections?.flatMap((s) => s.fields).length ?? 0} fields</span>
            </div>
            {form.description && (
              <p className="mt-1 text-xs text-gray-500 truncate max-w-md">{form.description}</p>
            )}
          </div>
        </div>
      </div>

      <div className="mt-3 flex items-center space-x-2">
        <ScopedLink
          to={`/forms/${form.id}/edit`}
          data-testid={`form-edit-${form.id}`}
          className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
        >
          <PencilIcon className="h-3.5 w-3.5 mr-1" />
          Edit
        </ScopedLink>
        <button
          data-testid={`form-publish-btn-${form.id}`}
          onClick={() => onPublish(form)}
          className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-primary-700 bg-primary-100 hover:bg-primary-200"
        >
          <MapPinIcon className="h-3.5 w-3.5 mr-1" />
          Publish to Locations
        </button>
        <button
          onClick={() => onDelete(form.id)}
          disabled={form.title === 'Default Registration Form'}
          title={
            form.title === 'Default Registration Form'
              ? 'Default Registration Form cannot be archived.'
              : undefined
          }
          data-testid={`form-delete-${form.id}`}
          className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded text-red-700 bg-white hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <ArchiveBoxIcon className="h-3.5 w-3.5 mr-1" />
          Archive
        </button>
      </div>
    </div>
  );
};

// ── Main View ──────────────────────────────────────────────────────────────────

export interface OrgFormManagementViewProps {
  forms: FormDefinition[];
  locations: Location[];
}

const OrgFormManagementView: React.FC<OrgFormManagementViewProps> = ({ forms, locations }) => {
  const [publishModalForm, setPublishModalForm] = useState<FormDefinition | null>(null);
  const [deleteForm] = useDeleteFormMutation();
  const [pendingArchiveId, setPendingArchiveId] = useState<string | null>(null);

  const groupedForms = useMemo(() => {
    const groups: Record<string, FormDefinition[]> = {};
    for (const type of FORM_TYPE_ORDER) {
      groups[type] = forms.filter((f) => f.formType === type);
    }
    return groups;
  }, [forms]);

  const handleDelete = (formId: string) => {
    setPendingArchiveId(formId);
  };

  return (
    <>
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Form Templates
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Manage form templates and publish them to locations across your organization.
          </p>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4">
          <ScopedLink
            to="/forms/new"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
            Create Form
          </ScopedLink>
        </div>
      </div>

      {/* Grouped sections */}
      <div className="mt-6 space-y-8">
        {FORM_TYPE_ORDER.map((type) => {
          const typeForms = groupedForms[type] || [];
          const isSingleton = SINGLETON_TYPES.includes(type);

          return (
            <div key={type} data-testid={`form-section-${type}`}>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">{formTypeLabels[type]}</h3>

              {typeForms.length === 0 ? (
                <div className="border border-dashed border-gray-300 rounded-lg p-6 text-center">
                  <DocumentTextIcon className="mx-auto h-8 w-8 text-gray-400" />
                  <p className="mt-2 text-sm text-gray-500">
                    {isSingleton
                      ? `No ${formTypeLabels[type].toLowerCase()} yet. Create one.`
                      : `No ${formTypeLabels[type].toLowerCase()} yet.`}
                  </p>
                  <ScopedLink
                    to="/forms/new"
                    className="mt-3 inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-primary-700 bg-primary-100 hover:bg-primary-200"
                  >
                    <PlusIcon className="h-4 w-4 mr-1" />
                    {isSingleton
                      ? 'Create'
                      : `Add ${formTypeLabels[type].replace(' Forms', ' Form')}`}
                  </ScopedLink>
                </div>
              ) : (
                <div className="space-y-3">
                  {typeForms.map((form) => (
                    <OrgFormCard
                      key={form.id}
                      form={form}
                      locations={locations}
                      onPublish={setPublishModalForm}
                      onDelete={handleDelete}
                    />
                  ))}

                  {/* Add more button for list types */}
                  {!isSingleton && (
                    <ScopedLink
                      to="/forms/new"
                      className="inline-flex items-center px-3 py-1.5 border border-dashed border-gray-300 text-xs font-medium rounded text-gray-600 hover:bg-gray-50 hover:border-gray-400"
                    >
                      <PlusIcon className="h-4 w-4 mr-1" />
                      Add {formTypeLabels[type].replace(' Forms', ' Form')}
                    </ScopedLink>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Publish Modal */}
      {publishModalForm && (
        <PublishToLocationsModal
          isOpen={!!publishModalForm}
          onClose={() => setPublishModalForm(null)}
          form={publishModalForm}
          locations={locations}
        />
      )}
      <ConfirmDialog
        isOpen={!!pendingArchiveId}
        onConfirm={async () => {
          if (pendingArchiveId) {
            await deleteForm(pendingArchiveId);
          }
          setPendingArchiveId(null);
        }}
        onCancel={() => setPendingArchiveId(null)}
        title="Archive Form"
        message="Are you sure you want to archive this form?"
        confirmLabel="Archive"
        variant="warning"
      />
    </>
  );
};

export { FormDistributions };
export default OrgFormManagementView;
